import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { XMLParser } from 'fast-xml-parser';
import { postSoapRequest } from '../../services/TypeAnnonce_service';
import LoaderAnnonce from '../LoaderAnnonce';
import { AxiosResponse } from 'axios';
import { TabStypeItem } from '../../models/typeAnnonce';
import TypeAnnoncePercentagesService from '../../services/TypeAnnoncePercentagesService';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useScrapedTarifs } from '../../hooks/useScrapedTarifs';
import { RefreshCw } from 'lucide-react';

const COLORS = {
  main: "#2A919D",
  secondary: "#5bc1d1",
  accent: "#F4A261",
  accentDark: "#e67921",
  bgLight: "#f7fafb",
  danger: "#dc3545",
  dangerHover: "#c82333"
};

function formatValue(val: any) {
  if (typeof val !== 'number' || isNaN(val)) return '0';
  if (val % 1 === 0) return val.toString();
  return val.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

const TypeAnnoncePercentages: React.FC = () => {
  const [responseJson, setResponseJson] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [percentages, setPercentages] = useState<{ [key: string]: any }>({});
  const [modified, setModified] = useState<boolean>(false);
  const [refreshingTarifs, setRefreshingTarifs] = useState<boolean>(false);

  // Hook de scraping
  const {
    basePrices,
    loading: scrapingLoading,
    refreshTarifs
  } = useScrapedTarifs();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await TypeAnnoncePercentagesService.getPercentages();
        const formattedData = response.data.reduce((acc: any, item: any) => {
          acc[item.nSType] = item;
          return acc;
        }, {});
        setPercentages(formattedData);
      } catch (err) {
        console.error('Erreur lors de la récupération des pourcentages:', err);
        toast.error('Erreur lors de la récupération des pourcentages');
      }
    };
    fetchData();
  }, []);

  const callSoapApi = async () => {
    try {
      const responseXml: AxiosResponse<any> = await postSoapRequest();
      const parser = new XMLParser();
      const jsonResponse = parser.parse(responseXml.data);
      setResponseJson(jsonResponse);
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors de l\'appel API SOAP:', err);
      setError('Une erreur est survenue');
      toast.error('Erreur lors de l\'appel API SOAP');
      setLoading(false);
    }
  };

  useEffect(() => {
    callSoapApi();
  }, []);

  const divideIntoGroups = (items: TabStypeItem[]) => {
    const groups: TabStypeItem[][] = [[], [], [], []];
    items.forEach(item => {
      const nType = item.nType;
      if (nType >= 1 && nType <= 4) {
        groups[nType - 1].push(item);
      }
    });
    return groups;
  };

  const handlePercentageChange = (nSType: number, sSType: string, field: string, value: number) => {
    setPercentages(prev => ({
      ...prev,
      [nSType]: {
        ...prev[nSType],
        sSType: sSType,
        [field]: value
      }
    }));
    setModified(true);
  };

  const hasNonZeroValues = () => {
    return Object.values(percentages).some((item: any) =>
      item && item.discountPercentage && parseFloat(item.discountPercentage) !== 0
    );
  };

  const resetAllPercentages = () => {
    const resetPercentages = { ...percentages };
    Object.keys(resetPercentages).forEach(key => {
      if (resetPercentages[key]) {
        resetPercentages[key].discountPercentage = 0;
      }
    });
    setPercentages(resetPercentages);
    setModified(true);
    toast.info('Tous les pourcentages ont été remis à zéro');
  };

  const saveAllChanges = async () => {
    try {
      await TypeAnnoncePercentagesService.savePercentages(percentages);
      toast.success('Toutes les modifications ont été enregistrées !');
      setModified(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde des pourcentages');
    }
  };

  // Rafraîchir les tarifs scrapés
  const handleRefreshTarifs = async () => {
    setRefreshingTarifs(true);
    try {
      await refreshTarifs();
      toast.success('Tarifs actualisés avec succès');
    } catch (err) {
      toast.error('Erreur lors de l\'actualisation');
    } finally {
      setRefreshingTarifs(false);
    }
  };

  if (loading || scrapingLoading) {
    return <LoaderAnnonce />;
  }

  const groups = responseJson
    ? divideIntoGroups(responseJson['SOAP-ENV:Envelope']['SOAP-ENV:Body']['Get_nSTypeResult']['tabStype'])
    : [[], [], [], []];

  const canReset = hasNonZeroValues();
  const reducedRadius = "9px";

  const inputVariants: Variants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { delay: i * 0.045, type: 'spring', stiffness: 100, damping: 13 }
    })
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, scale: 0.97, y: 35 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { delay: custom * 0.06, type: 'spring', stiffness: 80, damping: 14 }
    }),
    hover: { scale: 1.02, boxShadow: `0 4px 14px 0 ${COLORS.secondary}26` }
  };

  const buttonVariants: Variants = {
    initial: { scale: 1, backgroundColor: COLORS.main, color: "#fff" },
    hover: {
      scale: 1.06,
      backgroundColor: COLORS.accentDark,
      color: "#fff",
      boxShadow: `0 2px 10px 0 ${COLORS.accentDark}44`,
      transition: { type: "spring", stiffness: 200 }
    },
    tap: { scale: 0.97 }
  };

  const resetButtonVariants: Variants = {
    initial: { scale: 1, backgroundColor: COLORS.danger, color: "#fff" },
    hover: {
      scale: 1.06,
      backgroundColor: COLORS.dangerHover,
      color: "#fff",
      boxShadow: `0 2px 10px 0 ${COLORS.dangerHover}44`,
      transition: { type: "spring", stiffness: 200 }
    },
    tap: { scale: 0.97 }
  };

  const renderGroup = (group: TabStypeItem[], title: string) => (
    <div className="col-md-6 mb-4" key={title}>
      <h6 className="bg-vert px-3 py-2 color rounded-top">{title}</h6>
      <ul className="list-group border-0 bg-transparent">
        <AnimatePresence>
          {group.map((item, idx) => {
            const discount = percentages[item.nSType]?.discountPercentage || 0;
            const basePrice = basePrices[item.sSType] || 0;
            const priceMajorated = basePrice + discount;

            return (
              <motion.li
                key={idx}
                custom={idx}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                exit="hidden"
                className="list-group-item d-flex flex-column gap-2 mb-3 border-0 p-3"
                style={{
                  borderRadius: reducedRadius,
                  background: `linear-gradient(120deg, ${COLORS.secondary}12 0%, #fff 100%)`,
                  boxShadow: '0 2px 14px rgba(44,145,157,0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: '100%' }}
                  transition={{ delay: 0.1 + idx * 0.02, duration: 0.5, ease: 'easeOut' }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 3,
                    height: '100%',
                    borderRadius: `${parseInt(reducedRadius) - 1}px 0 0 ${parseInt(reducedRadius) - 1}px`,
                    background: `linear-gradient(180deg, ${COLORS.main} 30%, ${COLORS.accent} 100%)`,
                    zIndex: 1
                  }}
                />
                <strong
                  className="mb-1"
                  style={{
                    fontSize: '1.08em',
                    color: COLORS.main,
                    letterSpacing: "0.01em",
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  {item.sSType}
                </strong>
                <div className="row align-items-center" style={{ position: 'relative', zIndex: 2 }}>
                  <motion.div
                    className="col-12 col-md-7 mb-1"
                    custom={1}
                    variants={inputVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <label className="form-label fw-semibold small mb-1" style={{ color: COLORS.accentDark }}>
                      Majoration (€)
                    </label>
                    <motion.div
                      className="input-group"
                      initial={{ boxShadow: '0 0 0 0 transparent' }}
                      whileFocus={{ boxShadow: `0 0 0 4px ${COLORS.secondary}33` }}
                      transition={{ duration: 0.2 }}
                    >
                      <input
                        type="number"
                        className="form-control"
                        value={formatValue(percentages[item.nSType]?.discountPercentage)}
                        min={-1000}
                        max={1000}
                        step={0.01}
                        onChange={(e) => handlePercentageChange(item.nSType, item.sSType, 'discountPercentage', parseFloat(e.target.value))}
                        style={{
                          background: '#f9fbfd',
                          border: `2px solid ${COLORS.accent}`,
                          borderRadius: `${reducedRadius} 0 0 ${reducedRadius}`,
                          fontWeight: 600,
                          color: COLORS.main,
                          fontSize: '1.13em',
                          transition: 'border 0.16s',
                          width: '50%'
                        }}
                      />
                      <span
                        className="input-group-text"
                        style={{
                          border: `2px solid ${COLORS.accent}`,
                          background: COLORS.accent,
                          color: '#fff',
                          borderRadius: `0 ${reducedRadius} ${reducedRadius} 0`,
                          fontWeight: 700,
                          fontSize: '1.09em'
                        }}
                      >
                        €
                      </span>
                    </motion.div>
                    {discount !== 0 && basePrice > 0 && (
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                        <div
                          style={{
                            background: COLORS.secondary,
                            color: '#fff',
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '0.9em',
                            width: 'fit-content',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                          }}
                        >
                          PRIX INITIAL | {basePrice} €
                        </div>
                        <div
                          style={{
                            background: COLORS.secondary,
                            color: '#fff',
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '0.9em',
                            width: 'fit-content',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                          }}
                        >
                          PRIX ESTIMÉ | {priceMajorated.toFixed(2)} €
                        </div>
                      </div>
                    )}
                  </motion.div>
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    </div>
  );

  return (
    <>
      <div className="container" style={{ background: COLORS.bgLight, padding: '5px 5px 5px', borderRadius: reducedRadius }}>
        {/* Bouton actualiser discret en haut à droite */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
          <button
            onClick={handleRefreshTarifs}
            disabled={refreshingTarifs}
            style={{
              background: 'transparent',
              border: `1px solid ${COLORS.secondary}`,
              color: COLORS.main,
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: refreshingTarifs ? 'not-allowed' : 'pointer',
              fontSize: '0.85em',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              opacity: refreshingTarifs ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!refreshingTarifs) {
                e.currentTarget.style.background = COLORS.secondary;
                e.currentTarget.style.color = '#fff';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = COLORS.main;
            }}
          >
            <RefreshCw size={14} style={{ animation: refreshingTarifs ? 'spin 1s linear infinite' : 'none' }} />
            {refreshingTarifs ? 'Actualisation...' : 'Actualiser'}
          </button>
        </div>

        <div className="row">
          {renderGroup(groups[1], "MODIFICATION DE SOCIETE")}
          {renderGroup(groups[3], "DIVERS AUTRES")}
        </div>

        <div className="row">
          {renderGroup(groups[2], "CESSATION D'ACTIVITÉ")}
        </div>

        <div className="d-flex justify-content-center gap-3 mb-4 mt-4">
          <motion.button
            className="btn btn-lg px-4 py-2"
            onClick={resetAllPercentages}
            disabled={!canReset}
            variants={resetButtonVariants}
            initial="initial"
            whileHover={canReset ? "hover" : "initial"}
            whileTap={canReset ? "tap" : "initial"}
            animate="initial"
            style={{
              background: COLORS.danger,
              color: "#fff",
              border: "none",
              borderRadius: "15px",
              fontWeight: 600,
              fontSize: "1.13em",
              letterSpacing: "0.02em",
              boxShadow: canReset ? `0 4px 18px ${COLORS.accentDark}33` : "none",
              cursor: canReset ? "pointer" : "not-allowed",
              transition: "box-shadow 0.23s"
            }}
          >
            <span style={{ display: "inline-block", color: "#fff" }}>
              Remise à zéro
            </span>
          </motion.button>
          <motion.button
            className="btn btn-lg px-5 py-2"
            onClick={saveAllChanges}
            disabled={!modified}
            variants={buttonVariants}
            initial="initial"
            whileHover={modified ? "hover" : "initial"}
            whileTap={modified ? "tap" : "initial"}
            animate="initial"
            style={{
              background: COLORS.main,
              color: "#fff",
              border: "none",
              borderRadius: "15px",
              fontWeight: 600,
              fontSize: "1.13em",
              letterSpacing: "0.02em",
              boxShadow: modified ? `0 4px 18px ${COLORS.main}33` : "none",
              cursor: modified ? "pointer" : "not-allowed",
              transition: "box-shadow 0.23s"
            }}
          >
            <motion.span
              initial={{ scale: 1 }}
              animate={modified ? { scale: [1, 1.11, 1] } : { scale: 1 }}
              transition={modified ? { repeat: Infinity, duration: 1.6, ease: "easeInOut" } : {}}
              style={{ display: "inline-block", color: "#fff" }}
            >
              Enregistrer
            </motion.span>
          </motion.button>
        </div>
      </div>

      {/* CSS pour l'animation du refresh */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <ToastContainer />
    </>
  );
};

export default TypeAnnoncePercentages;